'use client';

import * as path from 'path';

import * as ENGINE from '@gnsx/genesys.js';
import { NavigationSettings } from '@gnsx/genesys.js';
import { observable } from 'mobx';
import { toast } from 'sonner';
import * as THREE from 'three';

import { DEFAULT_NAVMESH_DEBUG_COLOR } from '@/const';
import { configureSceneResources, getStorageProvider, saveScene } from '@/editor/core/sceneHandler';
import { setPropertyValueDirect } from '@/editor/core/universalAccessor';
import { editorScene, store } from '@/editor/store';

// Event emitter for NavMesh state changes
class NavMeshEventEmitter extends EventTarget {
  private static instance: NavMeshEventEmitter;

  static getInstance(): NavMeshEventEmitter {
    if (!this.instance) {
      this.instance = new NavMeshEventEmitter();
    }
    return this.instance;
  }

  emitNavMeshChanged() {
    this.dispatchEvent(new CustomEvent('navmesh-changed'));
  }

  emitDebugToggled() {
    this.dispatchEvent(new CustomEvent('navmesh-debug-toggled'));
  }
}

export { NavMeshEventEmitter };

const NAVMESH_DEBUG_VISUALIZATION_NAME = 'NavmeshDebugVisualization';

function applyNavMeshDebugMaterial(material: THREE.Material, color: THREE.Color): void {
  const mutableMaterial = material as THREE.Material & {
    color?: { set: (value: THREE.ColorRepresentation) => void };
    wireframe?: boolean;
    vertexColors?: boolean;
  };

  // DebugDrawer can provide per-vertex colors that make edge lines appear in a different color.
  // Disable vertex colors so one material color is applied consistently.
  if (typeof mutableMaterial.vertexColors === 'boolean') {
    mutableMaterial.vertexColors = false;
  }
  if (mutableMaterial.color?.set) {
    mutableMaterial.color.set(color);
  }
  mutableMaterial.needsUpdate = true;
}

function applyNavMeshDebugAppearance(debugVisualization: THREE.Object3D): void {
  const color = new THREE.Color(store.navMeshDebugColor || DEFAULT_NAVMESH_DEBUG_COLOR);

  debugVisualization.traverse(child => {
    child.raycast = () => {}; // Disable raycasting
    const meshChild = child as THREE.Mesh;
    if (!meshChild.material) {
      return;
    }
    if (Array.isArray(meshChild.material)) {
      meshChild.material.forEach(material => applyNavMeshDebugMaterial(material, color));
      return;
    }
    applyNavMeshDebugMaterial(meshChild.material, color);
  });
}

export type ProgressCallback = (stage: string, progressValue: number, message?: string) => void;

let navMeshSettings: NavigationSettings | null = null;

function createDefaultNavMeshSettings(): NavigationSettings {
  return observable(new NavigationSettings());
}

export function getNavMeshSettings(): NavigationSettings {
  if (!navMeshSettings) {
    navMeshSettings = createDefaultNavMeshSettings();
  }
  return navMeshSettings;
}

export function setNavMeshSettings(settings: NavigationSettings | null): void {
  navMeshSettings = settings ? observable(settings) : null;
}

/**
 * Sets a navmesh generation property with undo/redo.
 * NavigationSettings is not in the inspector property store, so setPropertyValue's
 * resource accessor cannot resolve it — we write directly to the live instance.
 */
export function setNavMeshSettingsPropertyValue(
  key: keyof NavMeshGenerationOptions,
  value: number
): void {
  const settings = getNavMeshSettings();
  const path = String(key);
  const oldValue = settings[key];
  if (oldValue === value) {
    return;
  }

  void store.undoRedoManager.recordAction(
    `Set ${path}`,
    () => { setPropertyValueDirect(settings, path, value); },
    () => { setPropertyValueDirect(settings, path, oldValue); },
  );
}

export function deriveNavMeshSettingsPath(scenePath: string): string {
  const sceneDirectory = path.dirname(scenePath);
  const sceneName = path.basename(scenePath, ENGINE.SCENE_NAME_EXT);
  return path.join(sceneDirectory, `${sceneName}${ENGINE.NAVIGATION_SETTINGS_RESOURCE_EXTENSION}`);
}

export async function importNavMeshSettings(): Promise<void> {
  setNavMeshSettings(null);

  const world = editorScene.world;
  if (!world) {
    return;
  }

  const settingsResourcePath = world.getSceneResources()?.navMeshSettings?.path;
  if (settingsResourcePath) {
    try {
      const resourceData = await ENGINE.resourceManager.loadResource<NavigationSettings>(
        ENGINE.AssetPath.fromString(settingsResourcePath)
      );
      if (resourceData?.instance) {
        setNavMeshSettings(resourceData.instance);
        return;
      }
      console.warn('[navmeshActions] NavMesh settings resource exists but load failed:', settingsResourcePath);
    } catch (error) {
      console.warn('[navmeshActions] Failed to import configured NavMesh settings:', settingsResourcePath, error);
    }
  }

  setNavMeshSettings(createDefaultNavMeshSettings());
}

async function saveNavMeshSettings(settings: NavigationSettings, scenePath: string): Promise<boolean> {
  try {
    const storageProvider = getStorageProvider();
    const settingsPath = deriveNavMeshSettingsPath(scenePath);
    const serializedData = ENGINE.WorldSerializer.serializeResource(settings);
    const settingsJson = JSON.stringify(serializedData, null, 2);

    await storageProvider.uploadFile(
      ENGINE.AssetPath.fromString(settingsPath),
      new Blob([settingsJson], { type: 'application/json' })
    );

    if (editorScene.world) {
      editorScene.world.setNavMeshSettingsResource(settingsPath);
      console.log('[navmeshActions] Configured navmesh settings resource in scene:', { settingsPath });
    }

    return true;
  } catch (error) {
    console.error('[navmeshActions] Failed to save NavMesh settings:', error);
    return false;
  }
}

export interface NavMeshGenerationOptions {
  /** Cell size (XZ plane resolution) */
  cellSize: number;
  /** Cell height (Y axis resolution) */
  cellHeight: number;
  /** Maximum slope angle in degrees */
  walkableSlopeAngle: number;
  /** Minimum ceiling height for agent */
  walkableHeight: number;
  /** Maximum step height agent can climb */
  walkableClimb: number;
  /** Agent radius (affects corridor width) */
  walkableRadius: number;
  /** Maximum edge length in world units */
  maxEdgeLength: number;
  /** Edge simplification tolerance */
  maxSimplificationError: number;
  /** Minimum area for isolated regions */
  minRegionArea: number;
  /** Merge regions smaller than this */
  mergeRegionArea: number;
  /** Maximum vertices per polygon */
  maxVertsPerPoly: number;
  /** Detail mesh sample distance */
  detailSampleDist: number;
  /** Maximum detail mesh error */
  detailSampleMaxError: number;
  /** Border size around the mesh */
  borderSize: number;
}

export type NavMeshPreset = {
  name: string;
  description: string;
} & NavMeshGenerationOptions;

export const NAVMESH_PRESETS: Record<string, NavMeshPreset> = {
  indoor: {
    name: 'Indoor',
    description: 'High precision for tight indoor spaces',
    cellSize: 0.1,
    cellHeight: 0.1,
    walkableRadius: 0.3,
    walkableHeight: 2.0,
    walkableClimb: 0.2,
    walkableSlopeAngle: 25,
    maxEdgeLength: 6,
    maxSimplificationError: 1.3,
    minRegionArea: 4,
    mergeRegionArea: 8,
    maxVertsPerPoly: 6,
    detailSampleDist: 6,
    detailSampleMaxError: 1,
    borderSize: 0
  },
  outdoor: {
    name: 'Outdoor',
    description: 'Optimized for large outdoor environments',
    cellSize: 0.5,
    cellHeight: 0.3,
    walkableRadius: 1.0,
    walkableHeight: 2.5,
    walkableClimb: 1.0,
    walkableSlopeAngle: 45,
    maxEdgeLength: 20,
    maxSimplificationError: 1.3,
    minRegionArea: 20,
    mergeRegionArea: 40,
    maxVertsPerPoly: 6,
    detailSampleDist: 6,
    detailSampleMaxError: 1,
    borderSize: 0
  },
  balanced: {
    name: 'Balanced',
    description: 'Good balance of quality and performance',
    cellSize: 0.2,
    cellHeight: 0.2,
    walkableRadius: 0.5,
    walkableHeight: 2.0,
    walkableClimb: 0.5,
    walkableSlopeAngle: 35,
    maxEdgeLength: 12,
    maxSimplificationError: 1.3,
    minRegionArea: 8,
    mergeRegionArea: 20,
    maxVertsPerPoly: 6,
    detailSampleDist: 6,
    detailSampleMaxError: 1,
    borderSize: 0
  }
};

/**
 * Get default NavMesh generation options
 */
export function getDefaultNavMeshOptions(): NavMeshGenerationOptions {
  return { ...NAVMESH_PRESETS.balanced };
}

/**
 * Apply preset to NavMesh options
 */
export function applyPresetToOptions(preset: NavMeshPreset, options: NavMeshGenerationOptions): NavMeshGenerationOptions {
  return {
    ...options,
    ...preset
  };
}

/**
 * Check if NavMesh functionality is available
 */
export function isNavMeshAvailable(): boolean {
  // Check if editorScene exists
  if (!editorScene) {
    return false;
  }

  // Check if world exists
  if (!editorScene.world) {
    return false;
  }

  // Try to get navigation server
  const navigationServer = editorScene.world.getNavigationServer();

  if (!navigationServer) {
    console.error('[navmeshActions] Navigation server is not available - getNavigationServer() returned null/undefined');
    console.log('[navmeshActions] World object:', editorScene.world);
    console.log('[navmeshActions] World methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(editorScene.world)));
    return false;
  }

  return true;
}

/**
 * Get NavMesh information from the current scene
 */
export function getNavMeshInfo() {
  // Return safe defaults if world is not available
  if (!editorScene?.world) {
    return {
      hasNavMesh: false,
      isDebugEnabled: false,
    };
  }

  const navigationServer = editorScene.world.getNavigationServer();

  // Check if debug visualization is currently visible in the scene
  const debugVisualization = editorScene.world.scene.getObjectByName(NAVMESH_DEBUG_VISUALIZATION_NAME);
  const isDebugEnabled = !!debugVisualization;

  return {
    hasNavMesh: navigationServer?.isReady() ?? false,
    isDebugEnabled,
  };
}

function navigationSettingsToRecastOptions(settings: NavigationSettings) {
  return {
    cs: settings.cellSize,
    ch: settings.cellHeight,
    walkableSlopeAngle: settings.walkableSlopeAngle,
    walkableHeight: settings.walkableHeight,
    walkableClimb: settings.walkableClimb,
    walkableRadius: settings.walkableRadius,
    maxEdgeLen: settings.maxEdgeLength,
    maxSimplificationError: settings.maxSimplificationError,
    minRegionArea: settings.minRegionArea,
    mergeRegionArea: settings.mergeRegionArea,
    maxVertsPerPoly: settings.maxVertsPerPoly,
    detailSampleDist: settings.detailSampleDist,
    detailSampleMaxError: settings.detailSampleMaxError,
    borderSize: settings.borderSize,
  };
}

/**
 * Generate NavMesh with the current NavigationSettings and progress callback
 */
export async function generateNavMesh(
  onProgress?: ProgressCallback,
  settings: NavigationSettings = getNavMeshSettings()
): Promise<boolean> {

  const navigationServer = editorScene.world?.getNavigationServer();
  if (!navigationServer) {
    console.error('[navmeshActions] NavMesh generation failed - Navigation server not available');
    console.error('[navmeshActions] editorScene:', editorScene);
    console.error('[navmeshActions] editorScene.world:', editorScene.world);

    if (editorScene.world) {
      console.error('[navmeshActions] World exists but getNavigationServer() returned:', navigationServer);
      console.error('[navmeshActions] Available world methods:', Object.getOwnPropertyNames(Object.getPrototypeOf(editorScene.world)));
    }

    toast.error('Navigation server not available', { duration: 4000 });
    return false;
  }

  console.log('[navmeshActions] Generating NavMesh from scene:', editorScene.world?.scene.name);

  try {
    onProgress?.('Initializing', 0, 'Preparing NavMesh generation...');

    navigationServer.updateOptions?.(navigationSettingsToRecastOptions(settings));

    onProgress?.('Collecting Geometry', 20, 'Collecting scene geometry...');

    // Generate NavMesh from scene
    await editorScene.world!.createNavigationMeshFromScene();

    onProgress?.('Saving', 90, 'Saving NavMesh to file...');

    // Export the generated NavMesh to file
    const exportSuccess = await exportNavMesh();
    if (!exportSuccess) {
      onProgress?.('Error', 0, 'Failed to save NavMesh file');
      return false;
    }

    onProgress?.('Saving', 92, 'Saving NavMesh generation settings...');

    const currentScenePath = store.project.currentScene;
    if (currentScenePath) {
      const settingsSaved = await saveNavMeshSettings(settings, currentScenePath);
      if (!settingsSaved) {
        onProgress?.('Error', 0, 'Failed to save NavMesh settings');
        return false;
      }
    }

    onProgress?.('Saving', 95, 'Saving scene with NavMesh resource...');

    // Auto-save the scene to persist the navmesh and settings resource paths
    await saveScene();

    onProgress?.('Complete', 100, 'NavMesh generated and saved successfully!');

    refreshNavMeshDebugVisualization();

    // Emit event that NavMesh state has changed
    NavMeshEventEmitter.getInstance().emitNavMeshChanged();

    return true;
  } catch (error) {
    console.error('[navmeshActions] Failed to generate NavMesh:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    toast.error(`Failed to generate NavMesh: ${errorMessage}`, { duration: 5000 });
    onProgress?.('Error', 0, `Error: ${errorMessage}`);
    return false;
  }
}

/**
 * Export NavMesh to file and configure scene resources
 */
export async function exportNavMesh(scenePath?: string): Promise<boolean> {
  const navigationServer = editorScene.world?.getNavigationServer();
  if (!navigationServer || !navigationServer.isReady()) {
    toast.error('No NavMesh to export', { duration: 4000 });
    return false;
  }

  try {
    const currentScenePath = scenePath || store.project.currentScene;
    if (!currentScenePath) {
      toast.error('No scene path available for export', { duration: 4000 });
      return false;
    }

    const result = await editorScene.world!.exportNavigationMesh(ENGINE.AssetPath.fromString(currentScenePath));

    if (result.success && result.data && result.filename) {
      // Save NavMesh file in the same directory as the scene
      const storageProvider = getStorageProvider();
      const sceneDirectory = path.dirname(currentScenePath);

      // Ensure the filename has the correct .navmesh extension
      let navmeshFilename = result.filename;
      if (!navmeshFilename.endsWith(ENGINE.NAVMESH_EXT)) {
        navmeshFilename += ENGINE.NAVMESH_EXT;
      }

      const navmeshPath = ENGINE.AssetPath.fromString(path.join(sceneDirectory, navmeshFilename));

      // Create blob from the exported data
      const navmeshBlob = new Blob([result.data], { type: 'application/octet-stream' });

      // Save the file to the project storage
      await storageProvider.uploadFile(navmeshPath, navmeshBlob);

      // Configure scene resources using the new SceneResource system
      if (editorScene.world) {
        const navmeshAssetPath = navmeshPath.initialPath;

        editorScene.world.setNavMeshResource(navmeshAssetPath);
        console.log('[navmeshActions] Configured navmesh resource in scene:', {
          navmeshPath: navmeshAssetPath
        });
      }

      // Update scene resources configuration
      await configureSceneResources();

      toast.success(`NavMesh exported as ${navmeshFilename}`, {
        description: `Saved to: ${sceneDirectory}\n\nSave the scene to persist resource paths.`,
        duration: 4000
      });
      return true;
    } else {
      toast.error('Failed to export NavMesh', { duration: 5000 });
      return false;
    }
  } catch (error) {
    console.error('[navmeshActions] Failed to export NavMesh:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    toast.error(`Failed to export NavMesh: ${errorMessage}`, { duration: 5000 });
    return false;
  }
}

/**
 * Import NavMesh from file
 */
export async function importNavMesh(file: File): Promise<boolean> {
  if (!editorScene.world) {
    toast.error('No world available for NavMesh import', { duration: 4000 });
    return false;
  }

  try {
    const fileData = await file.arrayBuffer();
    const success = await editorScene.world.importNavigationMeshFromData(fileData);

    if (success) {
      toast.success(`NavMesh imported from ${file.name}`);

      refreshNavMeshDebugVisualization();

      // Emit event that NavMesh state has changed
      NavMeshEventEmitter.getInstance().emitNavMeshChanged();

      return true;
    } else {
      toast.error('Failed to import NavMesh - file may be corrupted or incompatible', { duration: 5000 });
      return false;
    }
  } catch (error) {
    console.error('[navmeshActions] Failed to import NavMesh:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    toast.error(`Failed to import NavMesh: ${errorMessage}`, { duration: 5000 });
    return false;
  }
}

/**
 * Toggle NavMesh debug visualization
 */
export function toggleNavMeshDebug(): boolean {
  console.log('[navmeshActions] Attempting to toggle NavMesh debug...');

  const navigationServer = editorScene.world?.getNavigationServer();
  if (!navigationServer) {
    console.error('[navmeshActions] Toggle debug failed - Navigation server not available');
    console.error('[navmeshActions] editorScene.world:', editorScene.world);
    toast.error('Navigation server not available', { duration: 4000 });
    return false;
  }

  try {
    // Check if debug visualization currently exists in the scene
    const existingDebug = editorScene.world!.scene.getObjectByName(NAVMESH_DEBUG_VISUALIZATION_NAME);
    const isCurrentlyEnabled = !!existingDebug;

    if (isCurrentlyEnabled) {
      // Remove existing debug visualization
      if (existingDebug) {
        editorScene.world!.scene.remove(existingDebug);
      }
      toast.success('NavMesh debug visualization disabled');
    } else {
      // Create new debug visualization
      const debugVisualization = navigationServer.createDebugVisualization();
      if (debugVisualization) {
        debugVisualization.name = NAVMESH_DEBUG_VISUALIZATION_NAME;
        applyNavMeshDebugAppearance(debugVisualization);
        editorScene.world!.scene.add(debugVisualization);
        toast.success('NavMesh debug visualization enabled');
      } else {
        toast.error('Failed to create debug visualization - no NavMesh available', { duration: 4000 });
        return false;
      }
    }

    // Emit event that debug visualization state has changed
    NavMeshEventEmitter.getInstance().emitDebugToggled();

    return !isCurrentlyEnabled;
  } catch (error) {
    console.error('[navmeshActions] Failed to toggle NavMesh debug:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    toast.error(`Failed to toggle debug: ${errorMessage}`, { duration: 5000 });
    return false;
  }
}

/**
 * Rebuilds the debug overlay when it is visible, so it matches the current navmesh data.
 */
function refreshNavMeshDebugVisualization(): void {
  if (!editorScene.world) {
    return;
  }

  const existingDebug = editorScene.world.scene.getObjectByName(NAVMESH_DEBUG_VISUALIZATION_NAME);
  if (!existingDebug) {
    return;
  }

  const navigationServer = editorScene.world.getNavigationServer();
  if (!navigationServer) {
    return;
  }

  editorScene.world.scene.remove(existingDebug);

  const debugVisualization = navigationServer.createDebugVisualization();
  if (debugVisualization) {
    debugVisualization.name = NAVMESH_DEBUG_VISUALIZATION_NAME;
    applyNavMeshDebugAppearance(debugVisualization);
    editorScene.world.scene.add(debugVisualization);
  }
}

export function refreshNavMeshDebugVisualizationAppearance(): void {
  const debugVisualization = editorScene.world?.scene.getObjectByName(NAVMESH_DEBUG_VISUALIZATION_NAME);
  if (!debugVisualization) {
    return;
  }
  applyNavMeshDebugAppearance(debugVisualization);
}

/**
 * Clear current NavMesh from memory and delete the file
 */
export async function clearNavMesh(): Promise<boolean> {
  console.log('[navmeshActions] Attempting to clear NavMesh...');

  const navigationServer = editorScene.world?.getNavigationServer();
  if (!navigationServer) {
    console.error('[navmeshActions] Clear NavMesh failed - Navigation server not available');
    console.error('[navmeshActions] editorScene.world:', editorScene.world);
    toast.error('Navigation server not available', { duration: 4000 });
    return false;
  }

  try {
    // Remove debug visualization
    const existingDebug = editorScene.world!.scene.getObjectByName(NAVMESH_DEBUG_VISUALIZATION_NAME);
    const hadDebugVisualization = !!existingDebug;
    if (existingDebug) {
      editorScene.world!.scene.remove(existingDebug);
    }

    // Clear the NavMesh from memory
    navigationServer.clearNavigationMesh();

    if (editorScene.world) {
      editorScene.world.clearNavMeshResource();
      editorScene.world.clearNavMeshSettingsResource();
    }

    // Delete the NavMesh file from storage
    const currentScenePath = store.project.currentScene;
    if (currentScenePath) {
      try {
        const storageProvider = getStorageProvider();
        const sceneDirectory = path.dirname(currentScenePath);
        const sceneName = path.basename(currentScenePath, ENGINE.SCENE_NAME_EXT);
        const navmeshFilename = `${sceneName}${ENGINE.NAVMESH_EXT}`;
        const navmeshPath = ENGINE.AssetPath.fromString(path.join(sceneDirectory, navmeshFilename));

        // Check if file exists before trying to delete
        if (await storageProvider.exists(navmeshPath)) {
          await storageProvider.deleteItem(navmeshPath);
          console.log(`[navmeshActions] Deleted NavMesh file: ${navmeshFilename}`);
        }

        const settingsPath = deriveNavMeshSettingsPath(currentScenePath);
        const settingsAssetPath = ENGINE.AssetPath.fromString(settingsPath);
        if (await storageProvider.exists(settingsAssetPath)) {
          await storageProvider.deleteItem(settingsAssetPath);
          console.log(`[navmeshActions] Deleted NavMesh settings file: ${path.basename(settingsPath)}`);
        }
      } catch (fileError) {
        console.warn('[navmeshActions] Failed to delete NavMesh file:', fileError);
        // Don't fail the whole operation if file deletion fails
      }

      await saveScene();
    }

    toast.success('NavMesh cleared successfully');

    // Emit event that NavMesh state has changed
    NavMeshEventEmitter.getInstance().emitNavMeshChanged();

    // If debug visualization was removed, also emit debug toggle event
    if (hadDebugVisualization) {
      NavMeshEventEmitter.getInstance().emitDebugToggled();
    }

    return true;
  } catch (error) {
    console.error('[navmeshActions] Failed to clear NavMesh:', error);
    const errorMessage = error instanceof Error ? error.message : 'Unknown error';
    toast.error(`Failed to clear NavMesh: ${errorMessage}`, { duration: 5000 });
    return false;
  }
}
